import Divider from "components/divider";
import TeamView from "components/team_view";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { getEmployeesByAdminAndOrg } from "services/getEmployees";
import { createLegalResearchRequest, createResearchTeam, updateLegalResearchRequest } from "services/legalResearchService";
import { createProsecutorAssignment, deleteProsecutorAssignment, updateProsecutorAssignment } from "services/prosecutorAssignentService";
import { LEGAL_RESEARCH_REQUEST_STATUS } from "utils/constantValues";
import ActionAvatars from "./fragments/actionAvatars";

interface TeamMember {
    id: number;
    name: string;
    role: string;
    isTeamLeader: boolean;
}

const getInitials = (name: string) => {
    if (!name) return "";
    const parts = name.split(" ");
    if (parts.length === 1) {
        return parts[0].charAt(0).toUpperCase();
    }
    return (
        parts[0].charAt(0).toUpperCase() +
        parts[parts.length - 1].charAt(0).toUpperCase()
    );
};

export default function CoordinatorCreateRequest() {
    const { t } = useTranslation();
    const location = useLocation();
    const editData = location.state?.formState;

    const [selectedProsecutor, setSelectedProsecutor] =
        useState<TeamMember | null>(null);
    const [employees, setEmployees] = useState([]);
    const [task, setTask] = useState(editData?.research_task || "");
    const [teamId, setTeamId] = useState(0);
    const [teamName, setTeamName] = useState("");
    const [teamView, setTeamView] = useState(false);
    const { showToast } = useToast();
    const contextData = useAuth();
    const [teamMembers, setTeamMembers] = useState<TeamMember[]>(
        (editData?.prosecutor_assignments ?? []).map((member: any) => ({
            ...member,
            isTeamLeader: member.isTeamLeader ?? member.is_team_leader ?? false,
            id: member.prosecutor_user_id,
            name: member.prosecutor_name,
            role: "Prosecutor"
        }))
    );
    const [teams, setTeams] = useState<any>([]);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [editingTeamId, setEditingTeamId] = useState<number | null>(null);
    const [hiddenTeams, setHiddenTeams] = useState<any[]>([]);
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (editData?.prosecutor_assignments && editData.prosecutor_assignments.length > 0) {
            const existingTeams: any[] = [];
            editData.prosecutor_assignments.forEach((assignment: any) => {
                const teamName = assignment.research_team_name || 'Team 1';
                const researchTeamId =
                    typeof assignment.research_team === "object"
                        ? assignment.research_team?.id
                        : assignment.research_team;
                let existingTeam = existingTeams.find(t => t.team_name === teamName);
                if (!existingTeam) {
                    existingTeam = { id: researchTeamId, team_name: teamName, team_members: [] };
                    existingTeams.push(existingTeam);
                }
                existingTeam.team_members.push({
                    id: assignment.prosecutor_user_id,
                    assignmentId: assignment.id,
                    name: assignment.prosecutor_name,
                    role: 'Prosecutor',
                    isTeamLeader: assignment.is_team_leader || assignment.isTeamLeader || false
                });
            });
            setTeams(existingTeams);
        }
    }, [editData]);

    const handleRemoveMember = (id: number) => {
        const memberToRemove = teamMembers.find((member) => member.id === id);
        if (
            memberToRemove?.isTeamLeader &&
            teamMembers.filter((m) => m.isTeamLeader).length === 1
        ) {
            setValidationError(t("CannotRemoveOnlyTeamLeader"));
            return;
        }
        setTeamMembers(teamMembers.filter((member) => member.id !== id));
        setValidationError(null);
    };

    const handleToggleTeamLeader = (id: number) => {
        setTeamMembers((prevMembers) =>
            prevMembers.map((member) => {
                if (member.id === id) {
                    return { ...member, isTeamLeader: !member.isTeamLeader };
                } else {
                    return { ...member, isTeamLeader: false };
                }
            })
        );
        setValidationError(null);
    };

    const handleAddProsecutor = () => {
        if (
            selectedProsecutor &&
            !teamMembers.some((member) => member.id === selectedProsecutor.id)
        ) {
            const newId =
                teamMembers.length > 0
                    ? Math.max(...teamMembers.map((m) => m.id)) + 1
                    : 1;
            setTeamMembers((prevMembers) => [
                ...prevMembers,
                {
                    id: selectedProsecutor.id,
                    name: selectedProsecutor.name,
                    role: "Prosecutor",
                    isTeamLeader: prevMembers.length === 0,
                },
            ]);
            setSelectedProsecutor(null);
            setValidationError(null);
        } else if (
            teamMembers.some((member) => member.id === selectedProsecutor?.id)
        ) {
            alert(t("ProsecutorAlreadyAdded"));
        }
    };

    const handleAddTeam = () => {
        if (!teamName.trim()) {
            setValidationError(t("PleaseEnterTeamName"));
            return;
        }
        if (teamMembers.length === 0) {
            setValidationError(t("PleaseAddTeamMemberBeforeCreatingTeam"));
            return;
        }
        if (!teamMembers.some((member) => member.isTeamLeader)) {
            setValidationError(t("PleaseSelectTeamLeader"));
            return;
        }
        
        if (!editingTeamId && teams?.some((team: any) => team.team_name === teamName)) {
            setValidationError(t("TeamNameAlreadyExists"));
            return;
        }

        if (editingTeamId !== null) {
            const updatedTeam = {
                id: teams[0]?.id,
                team_name: teamName,
                team_members: [...teamMembers],
            };
            setTeams([...hiddenTeams, updatedTeam]);
            setHiddenTeams([]);
            setEditingTeamId(null);
        } else {
            setTeams((prevTeam: any) => [
                ...prevTeam,
                {
                    team_name: teamName,
                    team_members: [...teamMembers]
                }
            ]);
        }
        
        setTeamMembers([]);
        setTeamName("");
        setSelectedProsecutor(null);
        setValidationError(null);
    };
    
    const handleCancelEdit = () => {
        setTeams(hiddenTeams);
        setHiddenTeams([]);
        setEditingTeamId(null);
        setTeamMembers([]);
        setTeamName("");
        setSelectedProsecutor(null);
        setValidationError(null);
    };
    
    const handleEdit = (index: number) => {
        const teamToEdit = teams[index];
        const remainingTeams = teams.filter((_: any, i: number) => i !== index);
        setHiddenTeams(remainingTeams);
        setTeams([teamToEdit]);
        setEditingTeamId(0);
        setTeamMembers(teamToEdit.team_members);
        setTeamName(teamToEdit.team_name);
    };

    const handleRemoveTeam = () => {
        setTeams((prevTeam: any) => prevTeam.filter((_: any, index: number) => index != teamId));
    };

    useEffect(() => {
        getEmployeesByAdminAndOrg({
            bearer:
                contextData.token ??
                contextData?.user?.access_token ??
                contextData?.user.user?.access_token,
            org_unit: contextData?.user?.org_unit || "",
            admin_unit: contextData?.user?.admin_unit || "",
        })
            .then((data) => {
                setEmployees(
                    data
                        .filter((emp: any) =>
                            emp.roles?.some((role: string) => role === "LegalResearchProsecutor")
                        )
                        .map((emp: any) => ({
                            id: emp.user.id,
                            name: emp.user.first_name,
                            role: emp.roles[0] || "LegalResearchProsecutor",
                            isTeamLeader: false,
                        }))
                );
            })
            .catch((error) => {
                console.error("Error fetching:", error);
                showToast(t("FailedToFetchEmployees"), "error");
            });
    }, [getEmployeesByAdminAndOrg, setEmployees]);

    const handleSubmit = async () => {
        setValidationError(null);
        setSubmitting(true);

        if (!task.trim()) {
            setValidationError(t("PleaseEnterResearchTask"));
            setSubmitting(false);
            return;
        }

        if (!editData && teams.length === 0) {
            setValidationError(t("PleaseCreateAtLeastOneTeam"));
            setSubmitting(false);
            return;
        }

        const teamsWithLeader = teams.filter((team: any) =>
            team.team_members?.some((member: any) => member.isTeamLeader)
        );
        if (!editData && teamsWithLeader.length === 0) {
            setValidationError(t("EachTeamMustHaveTeamLeader"));
            setSubmitting(false);
            return;
        }

        try {
            let response;
            const data = new FormData();

            if (editData) {
                // --- EDIT: update the task, then sync each team and its members ---
                data.append("research_task", task);
                response = await updateLegalResearchRequest(editData.id, data, contextData.token);

                const keptAssignmentIds: string[] = [];

                for (const team of teams) {
                    // Existing teams keep their id; teams added during the edit are created.
                    let researchTeamId = team.id;
                    if (!researchTeamId) {
                        const teamData = new FormData();
                        teamData.append("team_name", team.team_name);
                        teamData.append("research_request", response.id.toString());
                        const teamResponse = await createResearchTeam(contextData.token, teamData);
                        researchTeamId = teamResponse.id;
                    }

                    for (const member of team.team_members) {
                        const prosecutorData = new FormData();
                        prosecutorData.append("research_request", response.id.toString());
                        prosecutorData.append("prosecutor_name", member.name);
                        prosecutorData.append("prosecutor_user_id", member.id.toString());
                        prosecutorData.append("team_type", "prosecutor_topic_team");
                        prosecutorData.append(
                            "is_team_leader",
                            member.isTeamLeader ? "true" : "false"
                        );
                        prosecutorData.append("research_team", researchTeamId.toString());

                        if (member.assignmentId) {
                            await updateProsecutorAssignment(member.assignmentId, prosecutorData);
                            keptAssignmentIds.push(member.assignmentId.toString());
                        } else {
                            await createProsecutorAssignment(prosecutorData);
                        }
                    }
                }

                // Remove assignments that were dropped during the edit.
                for (const assignment of editData.prosecutor_assignments || []) {
                    if (!keptAssignmentIds.includes(assignment.id.toString())) {
                        await deleteProsecutorAssignment(assignment.id);
                    }
                }
            } else {
                // --- CREATE: create the request, then create each team and its members ---
                data.append("requested_by", "internal");
                data.append("requested_by_id", contextData.user?.user_id);
                data.append("research_task", task);
                response = await createLegalResearchRequest(contextData.token, data);

                for (const team of teams) {
                    const teamData = new FormData();
                    teamData.append("team_name", team.team_name);
                    teamData.append("research_request", response.id.toString());
                    const teamResponse = await createResearchTeam(contextData.token, teamData);

                    for (const member of team.team_members) {
                        const prosecutorData = new FormData();
                        prosecutorData.append("research_request", response.id.toString());
                        prosecutorData.append("prosecutor_name", member.name);
                        prosecutorData.append("prosecutor_user_id", member.id.toString());
                        prosecutorData.append("team_type", "prosecutor_topic_team");
                        prosecutorData.append(
                            "is_team_leader",
                            member.isTeamLeader ? "true" : "false"
                        );
                        prosecutorData.append("research_team", teamResponse.id);
                        await createProsecutorAssignment(prosecutorData);
                    }
                }
            }

            showToast(t("TaskCreatedSuccess"), "success");
            navigate(`/conduct-legal-research/${response.id}`);
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToSendRequest"), "error");
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => { console.log("teams", teams) }, [teams]);

    return (
        <div className="rounded-lg bg-white w-[70%] p-8">
            <div className="flex items-center w-[50ch] gap-2">
                <h1 className="text-[#9D0303] text-lg font-semibold whitespace-nowrap flex-shrink-0">
                    {t("ResearchInformation")}
                </h1>
                <hr className="bg-[#E9E9E9] h-0.5 w-full border-0" />
            </div>
            <form>
                <div className="flex flex-col mt-4 gap-2">
                    <label className="text-[#747171]">{t("ResearchTask")}</label>
                    <input value={task} onChange={(e) => setTask(e.target.value)} className="bg-[#F2F2F2] p-3 rounded-lg" placeholder={t("TaskPlaceholder")} />
                </div>
                <div className="mb-6 mt-6">
                    <div className="flex items-center justify-between mb-3">
                        <label
                            htmlFor="select-prosecutor"
                            className="block text-base font-medium text-gray-700"
                        >
                            {t("SetupTeamMembers")}
                            <span className="text-red-500 ml-1">*</span>
                        </label>
                        <div className="flex gap-2">
                            {editingTeamId !== null && (
                                <button
                                    type="button"
                                    onClick={handleCancelEdit}
                                    className="px-4 py-2 rounded-lg font-medium text-sm bg-gray-500 text-white hover:bg-gray-600 transition-all"
                                >
                                    {t("CancelEdit")}
                                </button>
                            )}
                            {teamMembers.length > 0 && (
                                <span className="text-sm text-gray-500 my-auto">
                                    {t("MemberCountAdded", { count: teamMembers.length })}
                                </span>
                            )}
                            <button
                                type="button"
                                onClick={handleAddTeam}
                                disabled={!teamName.trim() || teamMembers.length === 0 || !teamMembers.some((m) => m.isTeamLeader)}
                                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                                    !teamName.trim() || teamMembers.length === 0 || !teamMembers.some((m) => m.isTeamLeader)
                                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                        : editingTeamId !== null
                                            ? "bg-green-600 text-white hover:bg-green-700 shadow-md"
                                            : "bg-[#AA232F] text-white hover:bg-[#8B1A1A] shadow-md"
                                }`}
                            >
                                {editingTeamId !== null ? t("UpdateTeam") : t("CreateTeamWithCount", { count: teams.length })}
                            </button>
                        </div>
                    </div>
                    <div className="flex flex-col mb-4 gap-2">
                        <label className="text-[#747171]">{t("TeamName")}</label>
                        <input value={teamName} onChange={(e) => setTeamName(e.target.value)} className="bg-[#F2F2F2] p-3 rounded-lg" placeholder={t("TeamNamePlaceholder")} />
                    </div>
                    <div className="relative mb-3">
                        <select
                            id="select-prosecutor"
                            className="appearance-none w-full p-3 border border-gray-300 rounded-md bg-[#F2F2F2] pr-10 focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent"
                            value={selectedProsecutor?.id || ""}
                            onChange={(e) =>
                                setSelectedProsecutor(
                                    employees.find(
                                        (employee: any) => employee.id === e.target.value
                                    ) ?? null
                                )
                            }
                        >
                            <option value="">{t("SelectProsecutor")}</option>
                            {employees.map((emp: any) => (
                                <option key={emp.id} value={emp.id}>
                                    {emp.name} - {emp.role}
                                </option>
                            ))}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-700">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={handleAddProsecutor}
                        disabled={!selectedProsecutor}
                        className={`mb-4 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                            !selectedProsecutor
                                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                : "bg-[#AA232F] text-white hover:bg-[#8B1A1A] shadow-md"
                        }`}
                    >
                        {t("AddSelectedProsecutorToTeam")}
                    </button>

                    <div className="space-y-3 max-h-60 overflow-y-auto pr-2 custom-scrollbar scrollbar-hide">
                        {validationError && (
                            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                                {validationError}
                            </div>
                        )}
                        {teamMembers.length === 0 && (
                            <p className="text-gray-500 text-sm italic">
                                {t("NoTeamMembersAddedYet")}
                            </p>
                        )}
                        {teamMembers.length > 0 && !teamMembers.some((m) => m.isTeamLeader) && (
                            <p className="text-amber-600 text-sm italic">
                                {t("PleaseSelectTeamLeaderWarning")}
                            </p>
                        )}
                        {teamMembers.map((member) => (
                            <div
                                key={member.id}
                                className="flex items-center bg-gray-50 border border-gray-200 rounded-lg p-3 shadow-sm"
                            >
                                <div className="w-10 h-10 rounded-full mr-3 bg-gray-700 text-white flex items-center justify-center font-bold text-sm select-none">
                                    {getInitials(member.name)}
                                </div>

                                <div className="flex-grow">
                                    <p className="font-semibold text-gray-800">
                                        {member.name}
                                    </p>
                                    <p className="text-sm text-gray-500">{member.role}</p>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        id={`team-leader-${member.id}`}
                                        checked={member.isTeamLeader}
                                        onChange={() => handleToggleTeamLeader(member.id)}
                                        className="accent-[#AA232F] form-checkbox h-5 w-5 text-[#AA232F] border-gray-300 rounded cursor-pointer"
                                    />
                                    <label
                                        htmlFor={`team-leader-${member.id}`}
                                        className={`text-sm font-medium ${member.isTeamLeader ? 'text-[#AA232F]' : 'text-gray-700'}`}
                                    >
                                        {member.isTeamLeader ? t("TeamLeaderCheck") : t("SetAsTeamLeader")}
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveMember(member.id)}
                                        className="text-gray-400 hover:text-red-500"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-5 w-5"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="#AA232F"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M6 18L18 6M6 6l12 12"
                                            />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                    {hiddenTeams && hiddenTeams.length > 0 && (
                        <div className="mt-6 p-4 bg-gray-100 rounded-lg">
                            <p className="text-gray-500 text-sm mb-3">
                                {t("OtherTeamsCount", { count: hiddenTeams.length })}
                            </p>
                            {hiddenTeams.map((team: any, idx: number) => (
                                <div key={idx} className="mt-3 flex bg-gray-200 rounded-lg border border-gray-300 opacity-60 p-2">
                                    <span className="text-sm font-medium my-auto">{team.team_name}</span>
                                    <span className="ml-2 text-xs text-gray-500 my-auto">
                                        ({t("MembersCount", { count: team.team_members?.length || 0 })})
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}

                    {teams &&
                        teams.length !== 0 &&
                        (editingTeamId === null ? teams : teams.slice(0, 1)).map((team: any, index: number) =>
                        (<div className="mt-6 mb-6 flex bg-gray-50 rounded-lg border border-gray-200">
                            <ActionAvatars
                                title={team.team_name}
                                users={team.team_members}
                                prosecutor={true}
                            />
                            {contextData?.user?.roles?.some((p) => p === "LegalResearchCoordinator") &&
                                <svg onClick={() => handleEdit(index)} className="my-auto mr-2 hover:bg-gray-300 rounded-lg cursor-pointer" width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z" fill="black" />
                                </svg>}
                            <svg onClick={() => { setTeamView(!teamView); setTeamId(index); }} className="mr-2 ml-auto my-auto cursor-pointer" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <mask id="mask0_9_31087" maskUnits="userSpaceOnUse" x="1" y="5" width="22" height="14">
                                    <path d="M12 18C17.523 18 22 12 22 12C22 12 17.523 6 12 6C6.477 6 2 12 2 12C2 12 6.477 18 12 18Z" fill="white" stroke="white" stroke-width="1.33333" stroke-linejoin="round" />
                                    <path d="M12 14.5C12.663 14.5 13.2989 14.2366 13.7678 13.7678C14.2366 13.2989 14.5 12.663 14.5 12C14.5 11.337 14.2366 10.7011 13.7678 10.2322C13.2989 9.76339 12.663 9.5 12 9.5C11.337 9.5 10.7011 9.76339 10.2322 10.2322C9.76339 10.7011 9.5 11.337 9.5 12C9.5 12.663 9.76339 13.2989 10.2322 13.7678C10.7011 14.2366 11.337 14.5 12 14.5Z" fill="black" stroke="black" stroke-width="1.33333" stroke-linejoin="round" />
                                </mask>
                                <g mask="url(#mask0_9_31087)">
                                    <path d="M0 0H24V24H0V0Z" fill="#EFC83D" />
                                </g>
                            </svg>
                            <button
                                type="button"
                                onClick={handleRemoveTeam}
                                className="text-gray-400 hover:text-red-500"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-5 w-5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="#AA232F"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>
                        </div>)
                        )
                    }
                    {teams && teamView &&
                        <TeamView teamMembers={teams?.[teamId]?.team_members} onClose={() => setTeamView(!teamView)} />}

                    <div className="flex flex-grow mt-8 gap-4">
                        <button 
                            onClick={() => navigate(-1)} 
                            className="flex-1 border border-gray-800 py-4 rounded-lg"
                        >
                            {t("Cancel")}
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={!task.trim() || (!editData && teams.length === 0) || submitting}
                            className={`flex-1 py-4 rounded-lg font-medium transition-all flex items-center justify-center ${
                                !task.trim() || (!editData && teams.length === 0) || submitting
                                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                    : "bg-[#212121] text-white hover:bg-[#3d3d3d]"
                            }`}
                        >
                            {submitting ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    {t("Submitting")}
                                </>
                            ) : (
                                t("Submit")
                            )}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
}